import { attendanceOpen, changedMarks, DEFAULT_TIMEZONE, defaultLabels, deskMay, usesCheckIn, weekdayLong, type AttendanceMode, type MyBusiness, type SessionNotBookedItem } from "@teamna/shared";
import { useState, type ReactNode } from "react";
import { useAddSessionExtra, useRemoveSessionExtra, useSessionNotBooked } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useMarkAttendance } from "@teamna/web-client/hooks/use-groups.ts";
import { useMe, useUpdateMe } from "@teamna/web-client/hooks/use-member.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { useBusinessSession } from "@teamna/web-client/hooks/use-staff.ts";
import { formatTime } from "@teamna/web-client/lib/format.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Button, ErrorState, Notice, Sheet, SkeletonList, TakeAttendance, showToast, type AttendancePerson } from "../components/index.ts";
import { HealthDetails } from "../owner/parent/health.tsx";
import { SomeoneElseSheet } from "./someone-else-sheet.tsx";
import { ArrowLeftIcon } from "../components/Icons.tsx";

/**
 * The route body for taking attendance, shared by the staff area (`/staff/sessions/:id/attendance`)
 * and the owner office (`/owner/schedule/:id/attendance`). It reads the session, keeps the chosen
 * way (cards or a list) on the account, and saves only the marks that changed.
 *
 * The API still decides who may mark: owners and admins any session, staff only their own, from
 * the session's day on. The screen says so before the person starts, rather than after a refusal.
 *
 * On a group level the register is the whole level (2026-10-22): the booked swimmers, then the
 * level's swimmers and waiting names who did not book (the Did not book list, usual day first).
 * Came on one of them adds them as Someone else came does (`from: "register"`): a walk-in booking
 * marked Came, or a visit for a waiting name. Absent on one of them stores nothing. When the
 * register was saved before, they open as Absent, so the screen opens on the check step.
 *
 * Where clients pick each class (`usesCheckIn`), the screen opens on check-in by name (I1): each tap
 * saves one mark at once with `method: "checkin"`. The cards and the list stay next to it and save
 * as `headcount` and `list`.
 */

/** The key of a not-booked row on the register. It is never a booking id. */
const notBookedKey = (item: Pick<SessionNotBookedItem, "kind" | "id">) => `not-booked:${item.kind}:${item.id}`;
export function TakeAttendanceScreen({
  business,
  businessLoading,
  sessionId,
  onExit,
  exitLabel,
  fillScreen = true,
}: {
  business: MyBusiness | null;
  businessLoading: boolean;
  sessionId: string;
  onExit: () => void;
  exitLabel: string;
  /** False inside the owner office shell. See `TakeAttendance`. */
  fillScreen?: boolean;
}) {
  const businessId = business?.businessId ?? null;
  const timezone = business?.timezone;
  const session = useBusinessSession(businessId, sessionId);
  const me = useMe();
  const updateMe = useUpdateMe();
  const mark = useMarkAttendance(businessId ?? "");
  const { shape } = useShape(businessId);
  // The choice shows at once; the account catches up in the background. A failed save of the
  // choice only means the question comes back next time.
  const [chosen, setChosen] = useState<AttendanceMode | null | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const removeExtra = useRemoveSessionExtra(businessId ?? "", sessionId);
  const addExtra = useAddSessionExtra(businessId ?? "", sessionId);
  // The level's swimmers who did not book, on a group session this person may mark. Private
  // sessions keep their one booked person; an open class answers an empty list.
  const mayMark = business?.staffRole !== "staff" || session.data?.isMine;
  // "Someone else came" and the level's list: not for the front desk (T1), which checks in the booked.
  const addsExtras = deskMay(business?.staffRole, "addSessionExtra", business?.frontDesk);
  const levelList = session.data?.kind === "group" && session.data.status === "scheduled" && mayMark && addsExtras;
  const notBooked = useSessionNotBooked(businessId, levelList ? sessionId : null);

  const frame = (body: ReactNode) => (
    <div className="mx-auto w-full max-w-app px-5 pb-8">
      <button type="button" onClick={onExit} className="-ml-1 mt-4 inline-flex min-h-11 items-center gap-1.5 pr-2 text-[14px] font-semibold text-accent-strong">
        <ArrowLeftIcon size={18} />
        {exitLabel}
      </button>
      <div className="pt-3">{body}</div>
    </div>
  );
  const blocked = (title: string, text: string) =>
    frame(
      <div className="flex flex-col gap-3 rounded-card border border-line bg-paper p-5">
        <h1 className="text-[20px]">{title}</h1>
        <p className="text-[15px] text-ink-2">{text}</p>
        <Button variant="secondary" onClick={onExit}>
          Back to {exitLabel}
        </Button>
      </div>,
    );

  if (businessLoading || session.isLoading || me.isLoading) return frame(<SkeletonList rows={4} />);
  if (!business) return blocked("No business", "This account does not run sessions at a business.");
  if (session.error || !session.data) {
    return frame(<ErrorState error={session.error} onRetry={() => void session.refetch()} />);
  }

  if (levelList && notBooked.isLoading && !notBooked.data) return frame(<SkeletonList rows={4} />);

  const data = session.data;
  const labels = business.labels ?? defaultLabels(business.sport);
  const now = new Date();
  if (data.status !== "scheduled") return blocked("This session is cancelled", "There is no attendance to take.");
  if (business.staffRole === "staff" && !data.isMine) {
    return blocked("Not your session", `Only the ${labels.staffSingular} on this session takes its attendance.`);
  }
  if (!attendanceOpen(new Date(data.startsAt), now, timezone)) {
    return blocked("Not today yet", "You can take attendance from the day of the session.");
  }

  const mode = chosen !== undefined ? chosen : (me.data?.user.attendanceMode ?? null);
  // Health notes (C2): the flag for everyone on the team. The sheet reads each person's answers;
  // the server sends them only to a reader the owner allows, and the flag alone to the rest.
  const flagged = data.roster.filter((entry) => entry.healthNote);
  const healthNotice =
    flagged.length > 0 ? (
      <Notice
        tone="warning"
        title={flagged.length === 1 ? "1 health note" : `${flagged.length} health notes`}
        action={
          <div>
            <Button variant="secondary" onClick={() => setHealthOpen(true)}>
              Read the health notes
            </Button>
          </div>
        }
      >
        <span dir="auto">{flagged.map((entry) => entry.name).join(", ")}</span>. Take care in this session.
      </Notice>
    ) : null;
  const ended = new Date(data.endsAt).getTime() <= now.getTime();

  const day = weekdayLong(notBooked.data?.weekday ?? 0);
  const others = levelList ? (notBooked.data?.items ?? []) : [];
  // A saved register (a mark, or someone added who came) means the rest did not come.
  const savedBefore = data.roster.some((entry) => entry.attendance) || (data.waitingExtras?.length ?? 0) > 0;
  const people: AttendancePerson[] = [
    ...data.roster.map((entry) => ({
      bookingId: entry.bookingId,
      name: entry.name,
      note: entry.note,
      attendance: entry.attendance ?? null,
      photoUrl: entry.photoUrl ?? null,
      extraId: entry.extraId ?? null,
      healthNote: entry.healthNote === true,
      firstClass: entry.firstClass ?? false,
      checkedInAt: entry.checkedInAt ?? null,
      autoAbsent: entry.autoAbsent ?? false,
      sessionsLeft: entry.sessionsLeft ?? null,
    })),
    ...others.map((item) => ({
      notBooked: true,
      bookingId: notBookedKey(item),
      name: item.name,
      note: null,
      detail: item.usualDay ? `Not booked. Usually comes on ${day}` : item.kind === "waiting" ? "Not booked. Waits for a parent" : "Not booked",
      attendance: savedBefore ? ("absent" as const) : null,
      sessionsLeft: item.sessionsLeft ?? null,
    })),
  ];

  return (
    <>
    <TakeAttendance
      key={data.id}
      title={`${data.groupName} ${formatTime(data.startsAt, timezone)}`}
      subtitle={`${data.venueName ? `${data.venueName}. ` : ""}${ended ? "Ended" : "Ends"} ${formatTime(data.endsAt, timezone)}`}
      people={people}
      mode={mode}
      onModeChange={(next) => {
        setChosen(next);
        updateMe.mutate({ attendanceMode: next });
      }}
      onSave={async (answers) => {
        // The marks first, against the roster this screen showed; then each one who came with no
        // booking, which adds to that roster. One add that fails does not stop the others: the
        // ones saved leave the list, and Save again adds the rest.
        const marks = changedMarks(data.roster, answers);
        const came = others.filter((row) => answers[notBookedKey(row)] === "present");
        // Nobody booked and nobody came: Absent on a not-booked row stores nothing, so there is
        // nothing to save and the session stays Not taken yet.
        if (data.roster.length === 0 && (data.waitingExtras?.length ?? 0) === 0 && came.length === 0) return "nothing";
        if (marks.length > 0) {
          await mark.mutateAsync({ sessionId: data.id, marks, roster: data.roster.map((entry) => entry.bookingId), method: mode === "list" ? "list" : "headcount" });
        }
        let failed: unknown = null;
        for (const item of came) {
          try {
            await addExtra.mutateAsync(
              item.kind === "participant"
                ? { kind: "participant", participantId: item.id, from: "register" }
                : { kind: "waiting", pendingParticipantId: item.id, from: "register" },
            );
          } catch (error) {
            failed ??= error;
          }
        }
        if (failed) throw failed;
        return undefined;
      }}
      onExit={onExit}
      exitLabel={exitLabel}
      participantLabel={{ one: labels.participantSingular, many: labels.participantPlural }}
      formatNow={() => formatTime(new Date().toISOString(), timezone)}
      fillScreen={fillScreen}
      checkIn={
        usesCheckIn(shape.classFill)
          ? {
              startsAt: new Date(data.startsAt),
              autoAbsentAt: data.autoAbsentAt ? new Date(data.autoAbsentAt) : null,
              ...(timezone ? { timeZone: timezone } : {}),
              onTap: async (person, next) => {
                // `expected`: the mark the list shows, its own last tap included, so a tap never writes over another device's mark unseen.
                await mark.mutateAsync({ sessionId: data.id, marks: [{ bookingId: person.bookingId, attendance: next, expected: person.attendance ?? null }], method: "checkin" });
              },
            }
          : undefined
      }
      // A failed read of the level never blocks the booked register: it says so, with a way to read
      // again. After a failed refresh the last list stays, and the notice says it can be old.
      notice={
        levelList && notBooked.error ? (
          <div className="flex flex-col gap-3">
          {healthNotice}
          <Notice
            tone="warning"
            title={`Could not read the ${labels.participantPlural} who did not book`}
            action={
              <Button variant="secondary" loading={notBooked.isFetching} onClick={() => void notBooked.refetch()}>
                Try again
              </Button>
            }
          >
            {notBooked.data
              ? `The ${labels.participantPlural} who did not book may be out of date.`
              : `Only the booked ${labels.participantPlural} are on this list now.`}
          </Notice>
          </div>
        ) : (healthNotice ?? undefined)
      }
      {...(addsExtras
        ? {
            extras: {
              waiting: data.waitingExtras ?? [],
              onAdd: () => setAdding(true),
              onRemove: (extraId: string) =>
                removeExtra.mutate(extraId, {
                  onSuccess: () => showToast("Removed from this session."),
                  onError: (error) => showToast(messageForError(error), { tone: "info" }),
                }),
            },
          }
        : {})}
    />
    {businessId && flagged.length > 0 ? (
      <Sheet open={healthOpen} onClose={() => setHealthOpen(false)} title="Health notes">
        <div className="flex flex-col gap-5">
          {flagged.map((entry) => (
            <div key={entry.bookingId} className="flex flex-col gap-2">
              <p className="text-[16px] font-semibold text-ink" dir="auto">
                {entry.name}
              </p>
              <HealthDetails businessId={businessId} participantId={entry.participantId} timezone={timezone ?? DEFAULT_TIMEZONE} />
            </div>
          ))}
        </div>
      </Sheet>
    ) : null}
    {businessId ? (
      <SomeoneElseSheet
        open={adding}
        onClose={() => setAdding(false)}
        businessId={businessId}
        sessionId={data.id}
        sessionTitle={`${data.groupName} ${formatTime(data.startsAt, timezone)}`}
        labels={labels}
      />
    ) : null}
    </>
  );
}
